import React, { useState, useCallback, useMemo } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { useIntl } from "react-intl";
import {
  IoIosCloseCircleOutline,
  IoIosCheckmarkCircleOutline,
} from "react-icons/io";
import { PiDownloadSimple } from "react-icons/pi";
import { saveAs } from "file-saver";
import Business from "../../../../newUXAssets/icons/Business";
import { IoInformationCircle } from "react-icons/io5";
import { IoDocumentTextOutline } from "react-icons/io5";
import {
  AreaBusiness,
  Header,
  HeaderLeft,
  TitleArea,
  Title,
  Subtitle,
  DateText,
  FooterContainer,
  DownloadSection,
  DownloadLink,
  ButtonsSection,
  RejectButton,
  ApproveButton,
  ViewerArea,
  IFrame,
  PreviewImage,
  StyledModal,
  ViewFooterContainer,
  ViewCloseButton,
  DisplayIcon,
  ObservationSection,
  ViewTopSection,
  ViewBottomSection,
  ObservationText,
  RejectedText,
  DoubleObservationDiv,
  ScrollableContent,
  AreaStatusAndDate,
  ModalHeader,
  InteractionArea,
  PackagePreviewList,
  PackagePreviewItem,
  PackageItemHeader,
  PackageItemName,
  PackageItemDownloadWrapper,
} from "./styles";

/** Extensões de imagem suportadas para preview */
const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".bmp",
  ".webp",
  ".svg",
];

const QuickPreviewModal = ({
  open,
  onClose,
  onApprove,
  onReject,
  onDelete,
  hasPermissionToDeleteOwnDocuments = false,
  urlForDownload,
  urlForView,
  title,
  subtitle,
  dateText,
  status = "PENDING",
  justification,
  executionObservation,
  observation,
  uploadOrigin,
  rejectionReason,
  isClientUX = false,
  items = [],
  documentPackage = false,
  flowName,
}) => {
  const intl = useIntl();
  const [isDownloading, setIsDownloading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const isPackage = documentPackage && items?.length > 0;

  const extractFileTypeFromUrl = useCallback((url) => {
    if (!url) return "unsupported";
    try {
      const urlObj = new URL(url);
      const contentDisposition = urlObj.searchParams.get(
        "response-content-disposition"
      );
      if (contentDisposition) {
        const decoded = decodeURIComponent(contentDisposition);
        const filenameMatch = decoded.match(/filename=["']?([^"';]+)["']?/i);
        if (filenameMatch) {
          const filename = filenameMatch[1].toLowerCase();
          if (filename.endsWith(".pdf")) return "pdf";
          if (IMAGE_EXTENSIONS.some((ext) => filename.endsWith(ext)))
            return "image";
        }
      }
      const urlPath = url.split("?")[0].toLowerCase();
      if (urlPath.endsWith(".pdf")) return "pdf";
      if (IMAGE_EXTENSIONS.some((ext) => urlPath.endsWith(ext))) return "image";
      return "unsupported";
    } catch (error) {
      console.warn("Erro ao extrair tipo de arquivo da URL:", error);
      return "unsupported";
    }
  }, []);

  const prepareFilePreview = useCallback(
    (downloadUrl, viewUrl) => {
      if (!downloadUrl) {
        return (
          <div style={{ color: "#999", fontFamily: "'Open Sans', sans-serif" }}>
            {intl.formatMessage({ id: "quick_preview.no_file" })}
          </div>
        );
      }
      const fileType = extractFileTypeFromUrl(downloadUrl);
      switch (fileType) {
        case "pdf":
          return <IFrame title="preview" src={viewUrl} />;
        case "image":
          return <PreviewImage alt="preview" src={downloadUrl} />;
        default:
          return (
            <div
              style={{
                color: "#999",
                textAlign: "center",
                padding: "20px",
                fontFamily: "'Open Sans', sans-serif",
              }}
            >
              {intl.formatMessage({ id: "quick_preview.no_file_preview" })}
            </div>
          );
      }
    },
    [extractFileTypeFromUrl, intl]
  );

  /** Lógica pura de download de um arquivo — reutilizada em download simples e download all */
  const downloadFile = useCallback(
    async (downloadUrl) => {
      if (!downloadUrl) return;
      try {
        const response = await fetch(downloadUrl, { method: "GET" });
        if (!response.ok)
          throw new Error(
            intl.formatMessage({ id: "quick_preview.download_failed" })
          );

        const blob = await response.blob();
        const urlPath = downloadUrl.split("?")[0];
        let filename =
          urlPath.substring(urlPath.lastIndexOf("/") + 1) ||
          intl.formatMessage({ id: "quick_preview.default_filename" });

        if (!filename.includes(".")) {
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("pdf")) filename += ".pdf";
          else if (contentType.includes("image/"))
            filename += `.${contentType.split("/")[1]?.split(";")[0] || "jpg"}`;
        }
        saveAs(blob, filename);
      } catch {
        try {
          const a = document.createElement("a");
          a.href = downloadUrl;
          const urlPath = downloadUrl.split("?")[0];
          const filename =
            urlPath.substring(urlPath.lastIndexOf("/") + 1) ||
            intl.formatMessage({ id: "quick_preview.default_filename" });
          a.setAttribute("download", filename);
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (_) {
          window.location.href = downloadUrl;
        }
      }
    },
    [intl]
  );

  /** Download de documento simples */
  const handleDownload = useCallback(
    async (downloadUrl = urlForDownload) => {
      if (!downloadUrl) return;
      setIsDownloading(true);
      try {
        await downloadFile(downloadUrl);
      } finally {
        setIsDownloading(false);
      }
    },
    [urlForDownload, downloadFile]
  );

  /** Download sequencial de todos os itens do pacote */
  const handleDownloadAll = useCallback(async () => {
    const downloadableItems = items.filter((item) => item.urlForDownload);
    if (!downloadableItems.length) return;

    setIsDownloading(true);
    try {
      for (const item of downloadableItems) {
        await downloadFile(item.urlForDownload);
      }
    } finally {
      setIsDownloading(false);
    }
  }, [items, downloadFile]);

  const downloadLabel = isDownloading
    ? intl.formatMessage({ id: "quick_preview.downloading" })
    : intl.formatMessage({ id: "quick_preview.download_file" });

  const downloadAllLabel = isDownloading
    ? intl.formatMessage({ id: "quick_preview.downloading" })
    : intl.formatMessage({ id: "quick_preview.download_all" });

  const displayStatus =
    status === "APPROVED" ? (
      <DisplayIcon status="APPROVED">
        <IoIosCheckmarkCircleOutline size={22} />
        {intl.formatMessage({ id: "quick_preview.approved" })}
      </DisplayIcon>
    ) : status === "REJECTED" ? (
      <DisplayIcon status="REJECTED">
        <IoIosCloseCircleOutline size={22} />
        {intl.formatMessage({ id: "quick_preview.rejected" })}
      </DisplayIcon>
    ) : (
      <></>
    );

  const displayTitle = useMemo(() => {
    const documentTitle =
      title === "document_package"
        ? intl.formatMessage({ id: "document_package" })
        : title;
    return flowName ? `${flowName} - ${documentTitle}` : documentTitle;
  }, [flowName, title, intl]);

  return (
    <StyledModal
      alterUx={isClientUX}
      visible={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
      centered
      closeIcon={<IoIosCloseCircleOutline size={24} color="#002244" />}
    >
      <ModalHeader />
      <ScrollableContent alterUx={isClientUX}>
        <Header>
          <HeaderLeft>
            <TitleArea>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Title>{displayTitle}</Title>
              </div>

              <AreaBusiness>
                {isClientUX ? (
                  <IoDocumentTextOutline
                    size={25}
                    color="rgba(17, 167, 217, 1)"
                  />
                ) : (
                  <Business />
                )}
                <Subtitle>{subtitle}</Subtitle>
              </AreaBusiness>
            </TitleArea>
          </HeaderLeft>

          <AreaStatusAndDate>
            <div>{displayStatus}</div>
            {dateText && (
              <DateText>
                {intl.formatMessage({ id: "quick_preview.sent_on" })} {dateText}
              </DateText>
            )}
          </AreaStatusAndDate>
        </Header>

        {isPackage ? (
          <PackagePreviewList>
            {items.map((item, index) => (
              <PackagePreviewItem key={item.documentConfigId ?? index}>
                <PackageItemHeader>
                  <PackageItemName>{item.fileName}</PackageItemName>

                  {/* {item.urlForDownload && (
                    <PackageItemDownloadWrapper
                      onClick={() => handleDownload(item.urlForDownload)}
                    >
                      <span>{intl.formatMessage({ id: "download_file" })}</span>
                      <DownloadLink
                        alterUx={isClientUX}
                        disabled={isDownloading}
                      >
                        <PiDownloadSimple size={18} />
                      </DownloadLink>
                    </PackageItemDownloadWrapper>
                  )} */}
                </PackageItemHeader>

                <ViewerArea>
                  {prepareFilePreview(item.urlForDownload, item.urlForView)}
                </ViewerArea>
              </PackagePreviewItem>
            ))}
          </PackagePreviewList>
        ) : (
          <ViewerArea>
            {prepareFilePreview(urlForDownload, urlForView)}
          </ViewerArea>
        )}

        {status === "PENDING" ? (
          <FooterContainer alterUx={isClientUX}>
            <ObservationSection>
              {intl.formatMessage({ id: "quick_preview.observations" })}{" "}
              <ObservationText>{executionObservation}</ObservationText>
            </ObservationSection>

            <InteractionArea>
              <DownloadSection>
                {isPackage ? (
                  <DownloadLink
                    alterUx={isClientUX}
                    onClick={handleDownloadAll}
                    disabled={isDownloading}
                  >
                    <PiDownloadSimple size={22} />
                    {downloadAllLabel}
                  </DownloadLink>
                ) : urlForDownload ? (
                  <DownloadLink
                    alterUx={isClientUX}
                    onClick={() => handleDownload()}
                    disabled={isDownloading}
                  >
                    <PiDownloadSimple size={22} />
                    {downloadLabel}
                  </DownloadLink>
                ) : (
                  <div />
                )}
              </DownloadSection>

              <ButtonsSection>
                {hasPermissionToDeleteOwnDocuments &&
                  status === "PENDING" &&
                  uploadOrigin === "PARTNER" && (
                    <>
                      <RejectButton
                        alterUx={isClientUX}
                        $isDelete
                        onClick={() => setDeleteModalOpen(true)}
                      >
                        <FaRegTrashAlt size={20} />
                        {intl.formatMessage({
                          id: "approval_documents.delete",
                        })}
                      </RejectButton>
                      <ConfirmationModal
                        modalIsOpen={deleteModalOpen}
                        setIsOpen={setDeleteModalOpen}
                        confirmationFunction={() => {
                          setDeleteModalOpen(false);
                          onDelete();
                        }}
                        cancelationFunction={() => setDeleteModalOpen(false)}
                        confirmationText={intl.formatMessage({
                          id: "approval_documents.delete",
                        })}
                        cancelText={intl.formatMessage({ id: "cancel" })}
                        description={
                          isPackage
                            ? intl.formatMessage({
                                id: "approval_package.delete_confirm",
                              })
                            : intl.formatMessage({
                                id: "approval_documents.delete_confirm",
                              })
                        }
                      />
                    </>
                  )}
                <RejectButton alterUx={isClientUX} onClick={onReject}>
                  {!isClientUX && <IoIosCloseCircleOutline size={22} />}
                  {intl.formatMessage({ id: "quick_preview.reject" })}
                </RejectButton>
                <ApproveButton alterUx={isClientUX} onClick={onApprove}>
                  {!isClientUX && <IoIosCheckmarkCircleOutline size={22} />}
                  {intl.formatMessage({ id: "quick_preview.approve" })}
                </ApproveButton>
              </ButtonsSection>
            </InteractionArea>
          </FooterContainer>
        ) : (
          <ViewFooterContainer>
            <ViewTopSection>
              {status === "REJECTED" && (
                <>
                  <ObservationSection>
                    {intl.formatMessage({ id: "quick_preview.observations" })}
                    <ObservationText>{executionObservation}</ObservationText>
                  </ObservationSection>
                  <ObservationSection IsDoubleLine>
                    <DoubleObservationDiv>
                      <IoInformationCircle
                        size={18}
                        color="rgba(98, 116, 142, 1)"
                      />
                      <RejectedText>
                        {intl.formatMessage({ id: "document_rejected" })}
                      </RejectedText>
                    </DoubleObservationDiv>
                    {justification && (
                      <div>
                        {intl.formatMessage({
                          id: "quick_preview.justification",
                        })}
                        <ObservationText>{justification}</ObservationText>
                      </div>
                    )}
                    {observation && (
                      <div>
                        {intl.formatMessage({
                          id: "quick_preview.additional.comments",
                        })}
                        <ObservationText>{observation}</ObservationText>
                      </div>
                    )}
                    {rejectionReason && (
                      <div>
                        {`${intl.formatMessage({ id: "rejection_reason" })}:`}
                        <ObservationText>{rejectionReason}</ObservationText>
                      </div>
                    )}
                  </ObservationSection>
                </>
              )}
            </ViewTopSection>

            {status === "APPROVED" && (
              <ObservationSection>
                {intl.formatMessage({ id: "quick_preview.observations" })}
                <ObservationText>{executionObservation}</ObservationText>
              </ObservationSection>
            )}

            <ViewBottomSection alterUx={isClientUX}>
              {isPackage ? (
                <DownloadLink
                  alterUx={isClientUX}
                  onClick={handleDownloadAll}
                  disabled={isDownloading}
                >
                  <PiDownloadSimple size={22} />
                  {downloadAllLabel}
                </DownloadLink>
              ) : urlForDownload ? (
                <DownloadLink
                  alterUx={isClientUX}
                  onClick={() => handleDownload()}
                  disabled={isDownloading}
                >
                  <PiDownloadSimple size={22} />
                  {downloadLabel}
                </DownloadLink>
              ) : (
                <div />
              )}
              <ViewCloseButton alterUx={isClientUX} onClick={onClose}>
                {!isClientUX && <IoIosCloseCircleOutline size={22} />}
                {intl.formatMessage({ id: "quick_preview.close" })}
              </ViewCloseButton>
            </ViewBottomSection>
          </ViewFooterContainer>
        )}
      </ScrollableContent>
    </StyledModal>
  );
};

export default QuickPreviewModal;
